import { BurgerIcon, CurecallLogo, DeprecatedLogoutIcon } from '@honestica/curecall-ui'
import SidebarTabs, { type TMenuTab } from './sidebar-tabs.component'
import { AreaLinks } from './sidebar-area-links.component'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'src/utils/tw-merge.util'
import { LocalizedLink } from './localized-link.component'
import { useState } from 'react'
import { useAuth } from '../../hooks/use-auth'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { EPermissionGrantActions, EPermissionGrantSubjects } from 'src/pages/configuration/permissions/_components/permissions.type'
import LocalTimeClock from './local-time-clock.component'

const SignoutButton = ({ isCollapsed, buttonLabel }: { isCollapsed: boolean; buttonLabel: string }) => {
  const { logout } = useAuth()

  const handleSignOut = () => {
    logout && logout()
  }

  return (
    <button
      className="c-flex c-mx-5 c-bg-secondary-100 c-rounded-md c-px-3 c-py-1 c-items-center c-justify-center c-group c-text-xs"
      onClick={handleSignOut}
    >
      <DeprecatedLogoutIcon className=" group-hover:c-fill-primary-800 c-w-5 c-h-5" />
      {!isCollapsed && (
        <span className="c-ml-1 c-bg-gradient-01 c-text-transparent c-bg-clip-text group-hover:c-text-primary-800">
          {buttonLabel}
        </span>
      )}
    </button>
  )
}

const ProjectName = ({ isCollapsed, projectName }: { isCollapsed: boolean; projectName: string }) => {
  return (
    <p
      className={twMerge(
        'c-text-white c-m-5 c-truncate c-p-0 c-font-bold c-font-nexa c-whitespace-normal c-shrink-0',
        isCollapsed && 'c-ml-3 c-mr-2 ',
      )}
    >
      {projectName}
    </p>
  )
}

const Sidebar = ({ menuTabs, backgroundClass }: { menuTabs: TMenuTab[]; backgroundClass?: string }) => {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const { project, isFeatureActivated, hasAnyPermission } = useCurrentProject()

  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    typeof window !== 'undefined' ? localStorage?.getItem('IS_MENU_COLLAPSED') === 'true' : false,
  )

  const isConfigurationAreaOpen =
    import.meta.env?.VITE_CONFIG_AREA_OPEN === 'true' ? true : currentUser?.isAdminOrSales

  return (
    <nav
      className={twMerge(
        `c-max-h-screen c-min-h-screen c-rounded-tr-3xl c-rounded-br-3xl c-flex c-flex-col c-overflow-scroll c-shrink-0`,
        isCollapsed ? 'c-max-w-[76px]' : 'c-max-w-[180px]',
        backgroundClass,
      )}
    >
      <div className={twMerge('c-flex c-items-center', isCollapsed && 'c-flex-col')}>
        <LocalizedLink
          to={`/${project.id}/medical/dashboard`}
          aria-label={t('components.navigation.curecall-logo')}
          className="c-mx-1 c-my-4"
        >
          <CurecallLogo className="c-text-white" isMinified={isCollapsed} />
        </LocalizedLink>
        <button
          aria-label={t('components.navigation.collapse-menu')}
          className="c-h-max c-px-1"
          onClick={() =>
            setIsCollapsed((prev) => {
              localStorage.setItem('IS_MENU_COLLAPSED', (!prev).toString())
              return !prev
            })
          }
        >
          <BurgerIcon />
        </button>
      </div>

      {(() => {
        const showConfiguration =
          !!isConfigurationAreaOpen &&
          isFeatureActivated('is-configuration-activated') &&
          hasAnyPermission([
            { subject: EPermissionGrantSubjects.CONFIGURATION, action: EPermissionGrantActions.GET },
            { subject: EPermissionGrantSubjects.USER_MANAGEMENT, action: EPermissionGrantActions.GET },
          ])
        return isFeatureActivated('is-hr-activated') || showConfiguration ? (
          <AreaLinks isCollapsed={isCollapsed} projectId={project.id} showConfiguration={showConfiguration} />
        ) : (
          <ProjectName isCollapsed={isCollapsed} projectName={project.name} />
        )
      })()}

      <LocalTimeClock isCollapsed={isCollapsed} />
      <SidebarTabs menuTabs={menuTabs} isCollapsed={isCollapsed} />

      <div className="c-mb-8 c-mt-auto">
        <SignoutButton isCollapsed={isCollapsed} buttonLabel={t('components.navigation.signout')} />
      </div>
    </nav>
  )
}

export default Sidebar
